/**
 * What a booking page actually locks on.
 *
 * ⚠️ `MeetingTypes.accessRequirement` IS NOT THE WHOLE ANSWER, and reading it
 * raw is a bug that has now been made three times. `syncAccessRequirement` on
 * the server derives that column from ATTACHED PRODUCTS only, so a page priced
 * in points at the MEETING TYPE level — `paymentSettings.priceUnit === 'points'`
 * — keeps the column at 'none'. `EntitlementService.resolve` promotes it
 * server-side and correctly answers `locked: true`; every client surface that
 * reads the column instead disagrees with the server about whether the page is
 * locked at all.
 *
 * What that disagreement cost, each time:
 *   - the header strip returned null, so a points page had no sign-in control
 *     and a returning customer had nowhere to sign in;
 *   - the entitlement guard was never mounted, so nothing explained why the
 *     booking would be refused;
 *   - the service flow made its own copy of this promotion, which is what
 *     turned one rule into three.
 *
 * ONE definition. Mirrors `EntitlementService.resolve` exactly — if that
 * changes, change this in the same commit.
 */

export type EffectiveAccessRequirement =
  | 'none'
  | 'point'
  | 'subscription'
  | 'point_or_subscription';

export const effectiveAccessRequirement = (
  meetingType: unknown
): EffectiveAccessRequirement => {
  const mt = (meetingType ?? {}) as Record<string, any>;
  const declared = (mt.accessRequirement ?? 'none') as EffectiveAccessRequirement;

  const settings = mt.paymentSettings;
  // Pricing a page in points IS a requirement to have them: the host set a
  // point price, not a card price.
  const pricedInPoints =
    settings?.enabled === true && settings?.priceUnit === 'points';

  return pricedInPoints && declared === 'none' ? 'point' : declared;
};

/** Whether anything at all must be held to book this page. */
export const isAccessLocked = (meetingType: unknown): boolean =>
  effectiveAccessRequirement(meetingType) !== 'none';

export default effectiveAccessRequirement;
